// §34.13 (M66.3) — the roster picker: who to send a file to.
//
// **Shell-drawn**, exactly like the Share picker and for the same reason (§19.4): the app that asked
// must not be able to influence who the user picks, or to learn who was offered. Explorer holds a
// capability that opens this and gets back an outcome; the roster never crosses into `apps/**`,
// because a list of who somebody knows is §26.9's business and not an app's.
//
// It says who is asking and what is about to be sent — one line, the file's name and size, never its
// content — and nothing else.
import { COPY } from '@/copy';
import { useKernel, useStoreValue } from '@/kernel/react';

export function ConnectionPicker() {
  const kernel = useKernel();
  const entry = useStoreValue(kernel.shellui.store, (state) => state.rosters[0]);
  if (!entry) return null;

  const candidates = entry.request.candidates;

  return (
    <div
      className="roster-picker"
      role="dialog"
      aria-label={COPY.offers.pickTitle}
      data-testid="roster-picker"
    >
      <h2 data-testid="roster-picker-title">{COPY.offers.pickTitle}</h2>
      {entry.appId !== null && (
        <p className="roster-from" data-testid="roster-picker-from">
          from {entry.appId}
        </p>
      )}
      <p className="roster-summary" data-testid="roster-picker-summary">
        {entry.request.summary}
      </p>

      {candidates.length === 0 ? (
        // §26.3 — there is no directory to search, so the only way to reach somebody new is a link.
        // Saying so beats an empty list that looks broken.
        <p className="roster-empty" data-testid="roster-picker-empty">
          {COPY.offers.pickEmpty}
        </p>
      ) : (
        <ul className="roster-list" data-testid="roster-picker-list">
          {candidates.map((candidate) => (
            <li key={candidate.ref}>
              <button
                type="button"
                className="roster-candidate"
                data-testid={`roster-target-${candidate.ref}`}
                data-status={candidate.status}
                onClick={() => kernel.shellui.acceptRoster(entry.request.id, candidate.ref)}
              >
                <span className="roster-name">{candidate.displayName}</span>
                {/* §26.2 in words as well as a colour, the way the Connections roster says it. */}
                <span className="roster-status">{COPY.social[candidate.status]}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="roster-actions">
        <button
          type="button"
          data-testid="roster-picker-cancel"
          onClick={() => kernel.shellui.cancelRoster(entry.request.id)}
        >
          {COPY.offers.pickCancel}
        </button>
      </div>
    </div>
  );
}
